import { RoleLevel } from './role';

/**
 * The shape of a role export file: a bare JSON array of these.
 *
 * Permissions are referenced by domain + permission identifier rather than by UUID, because every
 * environment generates its own identifiers. The role's own identifier is kept so that an import back
 * into the environment the file came from matches exactly, but an import falls back to matching on
 * name when that identifier is unknown.
 */
export interface RoleTransfer {
  id?: string;
  name: string;
  description?: string;
  scope: RoleTransferScope;
  /** Reported as a difference on import, but never applied. */
  ranking?: number;
  unassignable?: boolean;
  permissions: PermissionRef[];
}

export interface RoleTransferScope {
  level: RoleTransferLevel;
  shared: boolean;
}

export type RoleTransferLevel = 'DOMAIN' | 'TENANT' | 'ORGANISATION';

export interface PermissionRef {
  domain: string;
  permissionId: string;
}

export type RoleImportMatchType = 'ID' | 'NAME' | 'NONE';

export type RoleImportAction = 'CREATE' | 'UPDATE' | 'UNCHANGED' | 'BLOCKED';

export type RoleImportProblemSeverity = 'BLOCKING' | 'WARNING';

export type RoleImportProblemCode =
  | 'LEVEL_MISMATCH'
  | 'DUPLICATE_NAME_MATCH'
  | 'IAM_ADMIN_ROLE'
  | 'SELF_ASSIGNED_ROLE'
  | 'HIGHER_RANKED_ROLE'
  | 'PERMISSION_NOT_FOUND'
  | 'PERMISSION_NOT_AVAILABLE_AT_LEVEL'
  | 'PERMISSION_AMBIGUOUS';

export interface RoleImportProblem {
  code: RoleImportProblemCode;
  severity: RoleImportProblemSeverity;
  message: string;
  /** Set when the problem is about a single permission, in `domain.permission-id` form. */
  permission?: string;
}

/** A value the import would change, already rendered as text by the server. */
export interface ValueChange {
  from?: string;
  to?: string;
}

export interface RoleImportEntry {
  exportedId?: string;
  exportedName: string;
  matchedBy: RoleImportMatchType;
  targetRoleId?: string;
  action: RoleImportAction;
  nameChange?: ValueChange;
  descriptionChange?: ValueChange;
  sharedChange?: ValueChange;
  unassignableChange?: ValueChange;
  /** Reported for information only: the import never changes a role's ranking. */
  rankingDifference?: ValueChange;
  permissionsToAdd: PermissionRef[];
  permissionsToRemove: PermissionRef[];
  permissionsUnchangedCount: number;
  problems: RoleImportProblem[];
}

export interface RoleImportSummary {
  toCreate: number;
  toUpdate: number;
  unchanged: number;
  blocked: number;
}

/**
 * The difference between an import file and this environment: what a preview would do, or what an
 * import actually did.
 */
export interface RoleImportReport {
  roles: RoleImportEntry[];
  summary: RoleImportSummary;
}

export interface ExportRolesAction {
  roleIds: string[];
}

/** Renders a permission reference the way it is written everywhere else in the platform. */
export function toQualifiedPermissionId(permission: PermissionRef): string {
  return `${permission.domain}.${permission.permissionId}`;
}

/** Maps the level of the role list and detail pages onto the level used in an export file. */
export function toRoleTransferLevel(level: RoleLevel): RoleTransferLevel {
  return level.toUpperCase() as RoleTransferLevel;
}
